import { Box, Flex, Skeleton, Text, useMatchBreakpoints, Balance, Pool } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'

import { useVaultPoolByKey } from 'state/pools/hooks'
import { DeserializedLockedCakeVault, DeserializedLockedVaultUser, IsCakeVault, IsFlexible, IsLocked } from 'state/types'
import { getBalanceNumber } from '@pancakeswap/utils/formatBalance'
import { Token } from '@pancakeswap/sdk'
import useBUSDPrice from 'hooks/useBUSDPrice'

interface StakedCellProps {
  pool: Pool.DeserializedPool<Token>
  account: string
}

const StakedCell: React.FC<React.PropsWithChildren<StakedCellProps>> = ({ pool, account }) => {
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()

  // vault
  const vaultData = useVaultPoolByKey(pool.vaultKey)
  const {
    userData: {
      userShares,
      lastUserActionAmount,
      balance: { cakeAsBigNumber },
      isLoading,
    },
  } = vaultData
  const hasSharesStaked = userShares.gt(0)
  const isVaultWithShares = pool.vaultKey && hasSharesStaked

  // pool
  const { stakingTokenPrice, stakingToken } = pool

  const stakingTokenPriceUSD = useBUSDPrice(stakingToken)

  const labelText = `${pool.stakingToken.symbol} ${
    (vaultData as Pool.DeserializedPoolLockedVault<Token>).userData.locked
      ? t('Locked')
      : t('Staked')
  }`
  const userDataLoading = pool.vaultKey ? isLoading : !pool.userDataLoaded

  const isLocked = IsLocked(pool.vaultKey) && (vaultData.userData as DeserializedLockedVaultUser).locked
  const isCakeVault = IsCakeVault(pool.vaultKey)
  const stakedVaultAmount = isCakeVault ? cakeAsBigNumber : isLocked ? (vaultData as DeserializedLockedCakeVault).userData?.lockedAmount : lastUserActionAmount
  const stakedVaultBalance = getBalanceNumber(stakedVaultAmount, stakingToken.decimals)
  const stakedVaultBalanceUSD = getBalanceNumber(
    stakedVaultAmount.multipliedBy(stakingTokenPrice ?? stakingTokenPriceUSD?.toFixed(18)),
    stakingToken.decimals,
  )
    
  const hasStaked = account && (stakedVaultAmount.gt(0) || isVaultWithShares)

  return (
    <Pool.BaseCell
      role="cell"
      flex={
        IsFlexible(pool.vaultKey)
          ? '1 0 162px'
          : IsLocked(pool.vaultKey) && !hasStaked
          ? '1 0 120px'
          : '2 0 100px'
      }
    >
      <Pool.CellContent>
        <Text fontSize="12px" color="textSubtle" textAlign="left">
          {labelText}
        </Text>
        {userDataLoading && account ? (
          <Skeleton width="80px" height="16px" />
        ) : (
          <>
            <Flex>
              <Box mr="8px" height="32px">
                <Balance
                  mt="4px"
                  bold={!isMobile}
                  fontSize={isMobile ? '14px' : '16px'}
                  color={hasStaked ? 'primary' : 'textDisabled'}
                  decimals={hasStaked ? 2 : 1}
                  value={stakedVaultBalance}
                />
                {hasStaked ? (
                  <Balance
                    display="inline"
                    fontSize="12px"
                    color="textSubtle"
                    decimals={2}
                    prefix="~"
                    value={stakedVaultBalanceUSD}
                    unit=" USD"
                  />
                ) : (
                  <Text mt="4px" fontSize="12px" color="textDisabled">
                    0 USD
                  </Text>
                )}
              </Box>
            </Flex>
          </>
        )}
      </Pool.CellContent>
    </Pool.BaseCell>
  )
}

export default StakedCell
